import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import type { TFunction } from 'i18next'
import type { CharacterSheet } from '@rollcall/contracts'
import type { Api } from '../api.js'
import {
  ATTACKS,
  ATTACK_TABLE,
  BOTTOM,
  COINS,
  COMBAT,
  FOOTER,
  HEADER,
  HEADER_GRID,
  LEFT,
  PAGE,
  RESOURCE_ROW,
  SPELLS,
  at,
  type Box,
} from '../print-layout.js'
import {
  ATTACK_ROWS,
  fitLines,
  fitPt,
  printSheet,
  type PrintAbility,
  type PrintMark,
  type PrintResource,
  type PrintSheet,
} from '../print-sheet.js'
import {
  INNER_GAP,
  STROKE,
  abilityOutline,
  abilityTab,
  abilityTabBox,
  bannerPath,
  chamfered,
  iconFit,
  iconPath,
  inset,
  insetCuts,
  ribbon,
  type Cuts,
} from '../print-shapes.js'
import { SRD_NOTICE } from '../version-footer.js'
import coinsSvg from '../print/icons/coins.svg?raw'
import swordsSvg from '../print/icons/crossed-swords.svg?raw'
import heartSvg from '../print/icons/heart.svg?raw'
import personSvg from '../print/icons/person.svg?raw'
import shieldSvg from '../print/icons/shield.svg?raw'
import skullSvg from '../print/icons/skull.svg?raw'
import type { Heading } from './app.js'
import { useT } from './i18n-context.js'

/**
 * Печатный лист (D64): первая страница бланка.
 *
 * Лист — обычная страница приложения со строгой вёрсткой в миллиметрах.
 * Рамки рисует один SVG под всем листом, подписи и числа стоят поверх
 * него блоками HTML по координатам `print-layout.ts`. Так браузер сам
 * набирает кириллицу, переносит и обрезает строки, а печать отдаёт
 * в PDF то же, что видно на экране.
 *
 * Иконки — game-icons.net (CC BY 3.0, авторы в `ATTRIBUTION.md`
 * и в подвале листа). Сумка и сундук лежат рядом с остальными, но
 * на первой странице им места нет: в образце они стоят у рамок
 * «Снаряжение» и «Сокровища» второй страницы.
 */

const ICON = {
  person: iconPath(personSvg),
  shield: iconPath(shieldSvg),
  heart: iconPath(heartSvg),
  swords: iconPath(swordsSvg),
  coins: iconPath(coinsSvg),
  skull: iconPath(skullSvg),
} as const

/** Где рисунок щита лежит в своих 512 × 512: он узкий и высокий. */
const SHIELD_BOUNDS: Box = [106, 16, 300, 480]

const COIN_KEYS = ['cp', 'sp', 'ep', 'gp', 'pp'] as const

/* ── Фигуры ───────────────────────────────────────────────────────── */

/** Двойная рамка: толстый внешний контур и волосяной внутренний. */
function Double({
  box,
  cut,
  stroke = STROKE.heavy,
  gap = INNER_GAP,
  paper = false,
}: {
  readonly box: Box
  readonly cut: number | Cuts
  readonly stroke?: number
  readonly gap?: number
  /** Залить бумагой: рамка лежит поверх линии соседа и прячет её. */
  readonly paper?: boolean
}) {
  return (
    <>
      <polygon
        points={chamfered(box, cut)}
        strokeWidth={stroke}
        {...(paper ? { className: 'print-paper' } : {})}
      />
      <polygon
        points={chamfered(inset(box, gap), insetCuts(cut, gap))}
        strokeWidth={STROKE.hair}
      />
    </>
  )
}

/** Иконка в круге на углу рамки, как у образца. */
function IconCircle({ box, icon }: { readonly box: Box; readonly icon: string }) {
  const [x, y, w] = box
  const r = w / 2
  return (
    <>
      <circle
        cx={x + r}
        cy={y + r}
        r={r - STROKE.heavy / 2}
        strokeWidth={STROKE.heavy}
        className="print-paper"
      />
      <path d={icon} transform={iconFit(box, 0.6).transform} className="print-ink" />
    </>
  )
}

/** Всё, что не зависит от персонажа: рамки, линейки, иконки. */
function Frames() {
  const shieldOuter = iconFit(COMBAT.ac, 1, SHIELD_BOUNDS)
  const shieldInner = iconFit(COMBAT.ac, 0.84, SHIELD_BOUNDS)
  const [hpX, hpY, hpW, hpH] = inset(COMBAT.hp, INNER_GAP)
  const [hdX, hdY, hdW, hdH] = inset(COMBAT.hitDice, INNER_GAP)
  const [dsX, dsY, dsW, dsH] = COMBAT.deathSaves
  const dsMiddle = dsX + dsW / 2
  const [tableX, tableY, tableW] = ATTACKS.table
  const headLine = tableY + ATTACK_TABLE.head
  const tableBottom = headLine + ATTACK_ROWS * ATTACK_TABLE.row
  const [, bottomY, , bottomH] = BOTTOM.frame

  return (
    <svg
      className="print-frames"
      viewBox={`0 0 ${PAGE.width} ${PAGE.height}`}
      aria-hidden="true"
      focusable="false"
    >
      {/* Шапка: лента имени и рамка полей. */}
      <path d={bannerPath(HEADER.nameBanner)} strokeWidth={STROKE.medium} />
      <rect
        x={HEADER.info[0]}
        y={HEADER.info[1]}
        width={HEADER.info[2]}
        height={HEADER.info[3]}
        rx={3.2}
        strokeWidth={STROKE.heavy}
      />
      {HEADER_GRID.lines.map((line) =>
        HEADER_GRID.columns.map((column) => (
          <line
            key={`${line}-${column}`}
            x1={column}
            x2={column + HEADER_GRID.columnWidth}
            y1={line}
            y2={line}
            strokeWidth={STROKE.thin}
          />
        )),
      )}

      {/* Левый столбец. */}
      <Double box={LEFT.frame} cut={3} />
      {LEFT.rows.map((y) => {
        const bar: Box = [LEFT.rowLabel.x, y + 1.35, LEFT.rowLabel.w, LEFT.rowLabel.h]
        return (
          <g key={y}>
            <polygon
              points={ribbon(bar)}
              strokeWidth={STROKE.medium}
              className="print-paper"
            />
            <polygon points={ribbon(inset(bar, 0.9))} strokeWidth={STROKE.hair} />
            <Double
              box={[LEFT.rowValue.x, y, LEFT.rowValue.w, LEFT.rowValue.h]}
              cut={3}
              stroke={STROKE.medium}
              gap={0.9}
              paper
            />
          </g>
        )
      })}
      {LEFT.abilityTops.map((y) => {
        const box: Box = [LEFT.abilityBox.x, y, LEFT.abilityBox.w, LEFT.abilityBox.h]
        const right = box[0] + box[2]
        return (
          <g key={y}>
            <polygon
              points={abilityOutline(box)}
              strokeWidth={STROKE.heavy}
              className="print-paper"
            />
            <polygon
              points={abilityOutline(inset(box, 0.9), 5, 1.5)}
              strokeWidth={STROKE.hair}
            />
            <polyline points={abilityTab(box)} strokeWidth={STROKE.medium} />
            {/* Линия от рамки к столбцу навыков с точкой на конце. */}
            <polyline
              points={`${right - 2.75},${y + 2.75} ${right + 1.25},${y - 1.25} 85.6,${y - 1.25}`}
              strokeWidth={STROKE.light}
            />
            <circle cx={85.6} cy={y - 1.25} r={0.55} className="print-ink" />
          </g>
        )
      })}

      {/* Бой: КД, инициатива, скорость, хиты, кость хитов, спасброски от смерти. */}
      <Double box={COMBAT.frame} cut={3} />
      <IconCircle box={COMBAT.person} icon={ICON.person} />
      <path
        d={ICON.shield}
        transform={shieldOuter.transform}
        strokeWidth={STROKE.medium / shieldOuter.k}
        className="print-paper"
      />
      <path
        d={ICON.shield}
        transform={shieldInner.transform}
        strokeWidth={STROKE.hair / shieldInner.k}
      />
      <Double box={COMBAT.initiative} cut={3.5} />
      <Double box={COMBAT.speed} cut={3.5} />

      <Double box={COMBAT.hp} cut={3.5} />
      <line
        x1={hpX}
        x2={hpX + hpW}
        y1={hpY + 5}
        y2={hpY + 5}
        strokeWidth={STROKE.light}
      />
      <line
        x1={hpX}
        x2={hpX + hpW}
        y1={hpY + hpH - 5}
        y2={hpY + hpH - 5}
        strokeWidth={STROKE.light}
      />
      <line
        x1={hpX}
        x2={hpX + hpW}
        y1={hpY + hpH / 2}
        y2={hpY + hpH / 2}
        strokeWidth={STROKE.light}
      />
      <line
        x1={hpX + hpW / 2}
        x2={hpX + hpW / 2}
        y1={hpY + 5}
        y2={hpY + hpH - 5}
        strokeWidth={STROKE.light}
      />
      <IconCircle box={COMBAT.heart} icon={ICON.heart} />

      <Double box={COMBAT.hitDice} cut={3.5} />
      <line
        x1={hdX}
        x2={hdX + hdW}
        y1={hdY + 5}
        y2={hdY + 5}
        strokeWidth={STROKE.light}
      />
      <line
        x1={hdX}
        x2={hdX + hdW}
        y1={hdY + 5 + (hdH - 5) / 2}
        y2={hdY + 5 + (hdH - 5) / 2}
        strokeWidth={STROKE.light}
      />

      {/* Полоса спасбросков от смерти: провалы на чёрном, успехи на белом. */}
      <rect x={dsX} y={dsY} width={dsW} height={dsH} strokeWidth={STROKE.medium} />
      <rect x={dsX} y={dsY} width={dsW / 2} height={dsH} className="print-ink" />
      {[0, 1, 2].map((index) => (
        <g key={index}>
          <circle
            cx={dsX + 5.8 + index * 8.5}
            cy={dsY + dsH / 2}
            r={2.6}
            className="print-paper"
            stroke="none"
          />
          <circle
            cx={dsMiddle + 19.8 + index * 8.5}
            cy={dsY + dsH / 2}
            r={2.6}
            strokeWidth={STROKE.medium}
          />
        </g>
      ))}
      <polygon
        points={chamfered([dsMiddle - 5.5, dsY - 1.7, 11, 10.6], 3)}
        strokeWidth={STROKE.heavy}
        className="print-paper"
      />
      <path
        d={ICON.skull}
        transform={iconFit([dsMiddle - 5.5, dsY - 1.7, 11, 10.6], 0.72).transform}
        className="print-ink"
      />

      {/* Атаки. */}
      <Double box={ATTACKS.frame} cut={3} />
      <line
        x1={tableX}
        x2={tableX + tableW}
        y1={headLine}
        y2={headLine}
        strokeWidth={STROKE.light}
      />
      {Array.from({ length: ATTACK_ROWS }, (_, index) => (
        <line
          key={index}
          x1={tableX}
          x2={tableX + tableW}
          y1={headLine + (index + 1) * ATTACK_TABLE.row}
          y2={headLine + (index + 1) * ATTACK_TABLE.row}
          strokeWidth={STROKE.thin}
        />
      ))}
      {[
        ATTACK_TABLE.columns.bonus.x,
        ATTACK_TABLE.columns.damage.x,
        ATTACK_TABLE.columns.type.x,
      ].map((x) => (
        <line
          key={x}
          x1={x}
          x2={x}
          y1={tableY}
          y2={tableBottom}
          strokeWidth={STROKE.thin}
          strokeDasharray="0.3 0.6"
        />
      ))}
      <IconCircle box={ATTACKS.swords} icon={ICON.swords} />

      {/* Числа заклинателя. */}
      <Double box={SPELLS.frame} cut={3} />
      {SPELLS.rows.map((y) => (
        <Double
          key={y}
          box={[SPELLS.value.x, y, SPELLS.value.w, SPELLS.value.h]}
          cut={3.4}
          stroke={STROKE.medium}
          gap={0.9}
          paper
        />
      ))}

      {/* Деньги. */}
      <Double box={COINS.frame} cut={3} />
      {COINS.rows.map((y) => (
        <g key={y}>
          <polygon
            points={chamfered(
              [
                COINS.tab.x,
                y + (COINS.value.h - COINS.tab.h) / 2,
                COINS.tab.w,
                COINS.tab.h,
              ],
              [1.6, 0, 0, 1.6],
            )}
            strokeWidth={STROKE.medium}
            className="print-paper"
          />
          <Double
            box={[COINS.value.x, y, COINS.value.w, COINS.value.h]}
            cut={3.2}
            stroke={STROKE.medium}
            gap={0.9}
            paper
          />
        </g>
      ))}
      <IconCircle box={COINS.icon} icon={ICON.coins} />

      {/* Нижняя рамка: счётчики и заметки. Делитель столбцов — всегда:
          свободный столбец уходит под заметки. */}
      <Double box={BOTTOM.frame} cut={3} />
      <line
        x1={(BOTTOM.columns[0] + BOTTOM.columnWidth + BOTTOM.columns[1]) / 2}
        x2={(BOTTOM.columns[0] + BOTTOM.columnWidth + BOTTOM.columns[1]) / 2}
        y1={bottomY + 2.5}
        y2={bottomY + bottomH - 2.5}
        strokeWidth={STROKE.thin}
      />
    </svg>
  )
}

/* ── Текст ────────────────────────────────────────────────────────── */

type SlotProps = {
  readonly box: Box
  /** Кегль в пунктах. */
  readonly size: number
  readonly kind?: 'label' | 'number' | 'text'
  readonly align?: 'start' | 'center' | 'end'
  readonly valign?: 'top' | 'middle' | 'bottom'
  readonly wrap?: boolean
  /** Не больше двух строк: что не влезло — многоточие в конце второй. */
  readonly clamp?: boolean
  readonly blank?: boolean
  /** Подпись без разрядки: там, где места нет совсем. */
  readonly tight?: boolean
  /** Путь величины листа: по нему тест сверяет число с экраном. */
  readonly path?: string
  readonly proficient?: boolean
  readonly testId?: string
  readonly children?: ReactNode
}

/** Блок текста в своей коробке бланка: что не влезло — многоточие. */
function Slot({
  box,
  size,
  kind = 'text',
  align = 'center',
  valign = 'middle',
  wrap = false,
  clamp = false,
  blank = false,
  tight = false,
  path,
  proficient,
  testId,
  children,
}: SlotProps) {
  const classes = [
    'print-slot',
    `print-${kind}`,
    `print-align-${align}`,
    `print-valign-${valign}`,
    ...(wrap ? ['print-wrap'] : []),
    ...(clamp ? ['print-clamp'] : []),
    ...(blank ? ['print-blank'] : []),
    ...(tight ? ['print-tight'] : []),
  ]
  return (
    <div
      className={classes.join(' ')}
      style={{ ...at(box), fontSize: `${size}pt` }}
      {...(path === undefined ? {} : { 'data-path': path })}
      {...(proficient === true ? { 'data-proficient': '' } : {})}
      {...(testId === undefined ? {} : { 'data-testid': testId })}
    >
      <span className="print-slot-text">{children}</span>
    </div>
  )
}

function Header({ view, t }: { readonly view: PrintSheet; readonly t: TFunction }) {
  const [bx, by, bw] = HEADER.nameBanner
  const fields: readonly (readonly [label: string, value: string, testId: string])[] = [
    [t('print.label.classLevel'), view.header.classLevel, 'print-class'],
    [t('print.label.subclass'), view.header.subclass, 'print-subclass'],
    [t('print.label.player'), view.header.player, 'print-player'],
    [t('print.label.species'), view.header.species, 'print-species'],
    [t('print.label.background'), view.header.background, 'print-background'],
    [t('print.label.originFeat'), view.header.originFeat, 'print-origin-feat'],
    [t('print.label.alignment'), view.header.alignment, 'print-alignment'],
    [t('print.label.xp'), view.header.xp, 'print-xp'],
    [t('sheet.code'), view.header.code, 'print-code'],
  ]
  const width = HEADER_GRID.columnWidth
  // Имя длиннее ленты переносится на вторую строку, а не мельчает
  // до нечитаемого: в ленте место под две строки есть.
  const name = fitLines(view.name, bw - 8, 16, 11, 7, 'label')
  return (
    <>
      <Slot
        box={[bx + 4, by + 3.2, bw - 8, 11]}
        size={name.size}
        kind="label"
        wrap={name.lines === 2}
        clamp={name.lines === 2}
        testId="print-name"
      >
        {view.name}
      </Slot>
      <Slot box={HEADER.nameLabel} size={6.5} kind="label">
        {t('print.label.name')}
      </Slot>
      {fields.map(([label, value, testId], index) => {
        const column = HEADER_GRID.columns[index % 3] ?? 0
        const line = HEADER_GRID.lines[Math.floor(index / 3)] ?? 0
        return (
          <div key={testId}>
            <Slot
              box={[column, line - 4.4, width, 4.2]}
              size={fitPt(value, width, 9, 5.5)}
              align="start"
              valign="bottom"
              testId={testId}
            >
              {value}
            </Slot>
            <Slot
              box={[column, line + 0.35, width, 2.6]}
              size={fitPt(label, width, 5.5, 4.5, 'label')}
              kind="label"
              align="start"
            >
              {label}
            </Slot>
          </div>
        )
      })}
    </>
  )
}

/** Отметка владения: ромб у спасброска, круг у навыка — как у образца. */
function Mark({
  box,
  diamond,
  filled,
}: {
  readonly box: Box
  readonly diamond: boolean
  readonly filled: boolean
}) {
  return (
    <svg className="print-mark" style={at(box)} viewBox="0 0 10 10" aria-hidden="true">
      {diamond ? (
        <polygon
          points="5,0.8 9.2,5 5,9.2 0.8,5"
          strokeWidth={1.1}
          className={filled ? 'print-ink-mark' : 'print-paper'}
        />
      ) : (
        <circle
          cx={5}
          cy={5}
          r={3.9}
          strokeWidth={1.1}
          className={filled ? 'print-ink-mark' : 'print-paper'}
        />
      )}
    </svg>
  )
}

function MarkRow({
  mark,
  top,
  diamond,
}: {
  readonly mark: PrintMark
  readonly top: number
  readonly diamond: boolean
}) {
  const { x, w, row } = LEFT.skills
  const nameX = x + 10.6
  return (
    <>
      <Mark
        box={[x + 0.4, top + 0.7, 2.4, 2.4]}
        diamond={diamond}
        filled={mark.proficient}
      />
      <Slot
        box={[x + 3.6, top, 6, row - 0.35]}
        size={7.5}
        kind="number"
        valign="bottom"
        blank
        path={mark.path}
        proficient={mark.proficient}
      >
        {mark.value}
      </Slot>
      <Slot
        box={[nameX, top, x + w - nameX, row]}
        size={fitPt(mark.label, x + w - nameX, 7.5, 5, 'upper')}
        kind="text"
        align="start"
        testId="print-skill-name"
      >
        <span className="print-upper">{mark.label}</span>
      </Slot>
    </>
  )
}

function Ability({
  ability,
  top,
}: {
  readonly ability: PrintAbility
  readonly top: number
}) {
  const box: Box = [LEFT.abilityBox.x, top, LEFT.abilityBox.w, LEFT.abilityBox.h]
  const [x, y, w, h] = box
  const rows = [ability.save, ...ability.skills]
  return (
    <div data-ability={ability.name}>
      <Slot box={abilityTabBox(box)} size={8} kind="number" testId="print-score">
        {ability.score}
      </Slot>
      {/* Путь — у модификатора: экран под этим путём показывает крупно
          именно его, а значение пишет мелко приписью. */}
      <Slot
        box={[x, y + 5, w, 9.5]}
        size={18}
        kind="number"
        path={ability.path}
        testId="print-mod"
      >
        {ability.mod}
      </Slot>
      <Slot
        box={[x + 1, y + h - 5.4, w - 2, 4.2]}
        size={fitPt(ability.label, w - 2, 7, 5, 'label')}
        kind="label"
      >
        {ability.label}
      </Slot>
      {rows.map((mark, index) => (
        <MarkRow
          key={mark.path}
          mark={mark}
          top={y + 0.1 + index * LEFT.skills.row}
          diamond={index === 0}
        />
      ))}
    </div>
  )
}

function LeftColumn({ view, t }: { readonly view: PrintSheet; readonly t: TFunction }) {
  const rows: readonly (readonly [
    label: string,
    value: string,
    path: string | null,
  ])[] = [
    [t('sheet.proficiencyBonus'), view.proficiencyBonus, 'proficiency_bonus'],
    [t('print.label.inspiration'), view.inspiration, null],
    [t('sheet.passivePerception'), view.passivePerception, 'passive.perception'],
  ]
  return (
    <>
      {rows.map(([label, value, path], index) => {
        const y = LEFT.rows[index] ?? 0
        const labelWidth = LEFT.rowLabel.w - 8
        return (
          <div key={label}>
            <Slot
              box={[LEFT.rowValue.x, y, LEFT.rowValue.w, LEFT.rowValue.h]}
              size={13}
              kind="number"
              {...(path === null ? {} : { path })}
            >
              {value}
            </Slot>
            <Slot
              box={[LEFT.rowLabel.x + 3.5, y + 1.35, labelWidth, LEFT.rowLabel.h]}
              size={fitPt(label, labelWidth, 6.5, 4.5, 'label')}
              kind="label"
            >
              {label}
            </Slot>
          </div>
        )
      })}
      {view.abilities.map((ability, index) => (
        <Ability
          key={ability.name}
          ability={ability}
          top={LEFT.abilityTops[index] ?? 0}
        />
      ))}
    </>
  )
}

function Combat({ view, t }: { readonly view: PrintSheet; readonly t: TFunction }) {
  const [acX, acY, acW] = COMBAT.ac
  const [hpX, hpY, hpW, hpH] = inset(COMBAT.hp, INNER_GAP)
  const half = hpW / 2
  const middle = hpY + hpH / 2
  const [hdX, hdY, hdW, hdH] = inset(COMBAT.hitDice, INNER_GAP)
  const hdMiddle = hdY + 5 + (hdH - 5) / 2
  const [dsX, dsY, dsW] = COMBAT.deathLabel
  const square = (
    box: Box,
    value: string,
    label: string,
    path: string,
    testId: string,
  ) => {
    const [x, y, w, h] = box
    return (
      <>
        <Slot
          box={[x, y + 2.2, w, 10]}
          size={17}
          kind="number"
          path={path}
          testId={testId}
        >
          {value}
        </Slot>
        <Slot
          box={[x + 1, y + h - 6, w - 2, 4.4]}
          size={fitPt(label, w - 2, 6.5, 4.5, 'label')}
          kind="label"
        >
          {label}
        </Slot>
      </>
    )
  }

  return (
    <>
      <Slot
        box={[acX, acY + 3.6, acW, 8]}
        size={18}
        kind="number"
        path="ac"
        testId="print-ac"
      >
        {view.ac}
      </Slot>
      {/* Щит книзу сужается: подпись без разрядки и мельче остальных. */}
      <Slot box={[acX + 5, acY + 11.6, acW - 10, 5]} size={4.6} kind="label" wrap tight>
        {t('sheet.ac')}
      </Slot>
      {square(
        COMBAT.initiative,
        view.initiative,
        t('sheet.initiative'),
        'initiative',
        'print-initiative',
      )}
      {square(COMBAT.speed, view.speed, t('sheet.speed'), 'speed', 'print-speed')}

      {/* Хиты: максимум и сейчас, ниже — временные. */}
      <Slot box={[hpX, hpY, hpW, 5]} size={7} kind="label">
        {t('combat.hp')}
      </Slot>
      <Slot
        box={[hpX + 13, hpY + 5.4, half - 13, 7.8]}
        size={15}
        kind="number"
        path="hp.max"
        testId="print-hp-max"
      >
        {view.hp.max}
      </Slot>
      <Slot box={[hpX, middle - 3.6, half, 3.4]} size={5.5} kind="label">
        {t('print.label.hpMax')}
      </Slot>
      <Slot
        box={[hpX + half, hpY + 5.4, half, 7.8]}
        size={15}
        kind="number"
        testId="print-hp-current"
      >
        {view.hp.current}
      </Slot>
      <Slot box={[hpX + half, middle - 3.6, half, 3.4]} size={5.5} kind="label">
        {t('print.label.remaining')}
      </Slot>
      <Slot box={[hpX, middle + 0.3, half, 3.4]} size={5.5} kind="label">
        {t('print.label.total')}
      </Slot>
      <Slot box={[hpX + half, middle + 0.3, half, 3.4]} size={5.5} kind="label">
        {t('print.label.remaining')}
      </Slot>
      <Slot
        box={[hpX + half, middle + 3.6, half, 7]}
        size={13}
        kind="number"
        testId="print-hp-temp"
      >
        {view.hp.temp}
      </Slot>
      <Slot box={[hpX, hpY + hpH - 5, hpW, 5]} size={7} kind="label">
        {t('combat.temp')}
      </Slot>

      {/* Кость хитов: всего и потрачено. */}
      <Slot
        box={[hdX, hdY, hdW, 5]}
        size={fitPt(t('combat.hitDie'), hdW, 6.5, 4.5, 'label')}
        kind="label"
      >
        {t('combat.hitDie')}
      </Slot>
      <Slot
        box={[hdX, hdY + 5.6, hdW, hdMiddle - hdY - 9.6]}
        size={fitPt(view.hitDice.total, hdW - 2, 12, 7, 'number')}
        kind="number"
        testId="print-hit-dice"
      >
        {view.hitDice.total}
      </Slot>
      <Slot box={[hdX, hdMiddle - 3.6, hdW, 3.4]} size={5.5} kind="label">
        {t('print.label.total')}
      </Slot>
      <Slot
        box={[hdX, hdMiddle + 0.6, hdW, hdY + hdH - hdMiddle - 4.6]}
        size={12}
        kind="number"
        testId="print-hit-dice-spent"
      >
        {view.hitDice.spent}
      </Slot>
      <Slot box={[hdX, hdY + hdH - 3.8, hdW, 3.4]} size={5.5} kind="label">
        {t('print.label.spent')}
      </Slot>

      {/* Спасброски от смерти приложение не ведёт: полоса — для карандаша. */}
      <Slot box={[dsX, dsY, 26, 3.5]} size={5} kind="label" align="start">
        {t('print.label.failures')}
      </Slot>
      <Slot box={[dsX + 22, dsY, dsW - 44, 3.5]} size={6.5} kind="label">
        {t('print.label.deathSaves')}
      </Slot>
      <Slot box={[dsX + dsW - 26, dsY, 26, 3.5]} size={5} kind="label" align="end">
        {t('print.label.successes')}
      </Slot>
    </>
  )
}

function Attacks({ view, t }: { readonly view: PrintSheet; readonly t: TFunction }) {
  const { columns, head, row } = ATTACK_TABLE
  const [, tableY] = ATTACKS.table
  const top = (index: number) => tableY + head + index * row
  // Подпись столбца названий — правее круга с мечами: круг стоит
  // на углу рамки и закрывает начало строки заголовка.
  const nameHeadX = ATTACKS.swords[0] + ATTACKS.swords[2] + 1
  return (
    <>
      <Slot box={ATTACKS.title} size={11} kind="label">
        {t('combat.attacks')}
      </Slot>
      <Slot
        box={[nameHeadX, tableY, columns.bonus.x - nameHeadX, head]}
        size={5.5}
        kind="label"
        align="start"
      >
        {t('print.label.attackName')}
      </Slot>
      <Slot
        box={[columns.bonus.x, tableY, columns.bonus.w, head]}
        size={5.5}
        kind="label"
      >
        {t('combat.attack')}
      </Slot>
      <Slot
        box={[columns.damage.x, tableY, columns.damage.w, head]}
        size={5.5}
        kind="label"
      >
        {t('combat.damage')}
      </Slot>
      <Slot
        box={[columns.type.x, tableY, columns.type.w, head]}
        size={5.5}
        kind="label"
      >
        {t('print.label.damageType')}
      </Slot>
      {view.attacks.map((attack, index) => (
        <div key={attack.id} data-attack={attack.id}>
          <div
            className="print-slot print-stack print-align-start"
            style={at([columns.name.x + 0.6, top(index), columns.name.w - 1, row])}
          >
            <span
              className="print-slot-text print-text"
              style={{
                fontSize: `${fitPt(attack.name, columns.name.w - 1, 7.5, 5.5)}pt`,
              }}
              data-testid="print-attack-name"
            >
              {attack.name}
            </span>
            <span
              className="print-slot-text print-text print-note"
              style={{ fontSize: '5pt' }}
            >
              {attack.kind}
            </span>
          </div>
          <Slot
            box={[columns.bonus.x, top(index), columns.bonus.w, row]}
            size={9}
            kind="number"
            path={attack.bonusPath}
          >
            {attack.bonus}
          </Slot>
          <Slot
            box={[columns.damage.x, top(index), columns.damage.w, row]}
            size={fitPt(attack.damage, columns.damage.w - 1, 8.5, 6, 'number')}
            kind="number"
            testId="print-attack-damage"
          >
            {attack.damage}
          </Slot>
          <Slot
            box={[columns.type.x + 0.5, top(index), columns.type.w - 1, row]}
            size={fitPt(attack.type, columns.type.w - 1, 7, 5)}
            testId="print-attack-type"
          >
            {attack.type}
          </Slot>
        </div>
      ))}
      {view.attacksMore > 0 && (
        <Slot
          box={[
            ATTACKS.title[0] + ATTACKS.title[2] - 24,
            ATTACKS.title[1] + 1.4,
            24,
            3.2,
          ]}
          size={5}
          align="end"
          testId="print-attacks-more"
        >
          {t('print.more', { count: view.attacksMore })}
        </Slot>
      )}
    </>
  )
}

function Spellcasting({
  view,
  t,
}: {
  readonly view: PrintSheet
  readonly t: TFunction
}) {
  const spells = view.spellcasting
  const rows: readonly (readonly [
    label: string,
    hint: string | null,
    value: string,
    path: string | null,
  ])[] = [
    [t('print.label.spellAbility'), null, spells?.ability ?? '—', null],
    [
      t('spells.saveDc'),
      t('print.label.saveDcHint'),
      spells?.saveDc ?? '—',
      'spell.save_dc',
    ],
    [
      t('spells.attack'),
      t('print.label.attackHint'),
      spells?.attack ?? '—',
      'spell.attack',
    ],
  ]
  return (
    <>
      {rows.map(([label, hint, value, path], index) => {
        const y = SPELLS.rows[index] ?? 0
        const word = index === 0 && spells !== null
        return (
          <div key={label}>
            <div
              className="print-slot print-stack"
              style={at([SPELLS.label.x, y, SPELLS.label.w, SPELLS.value.h])}
            >
              <span
                className="print-slot-text print-label print-wrap"
                style={{ fontSize: '6.5pt' }}
              >
                {label}
              </span>
              {hint !== null && (
                <span
                  className="print-slot-text print-label print-wrap print-note"
                  style={{ fontSize: '4.3pt' }}
                >
                  {hint}
                </span>
              )}
            </div>
            <Slot
              box={[SPELLS.value.x, y, SPELLS.value.w, SPELLS.value.h]}
              size={word ? fitPt(value, SPELLS.value.w - 4, 9, 6, 'label') : 14}
              kind={word ? 'label' : 'number'}
              {...(path === null || spells === null ? {} : { path })}
              testId="print-spell-value"
            >
              {value}
            </Slot>
          </div>
        )
      })}
    </>
  )
}

function Coins({ view, t }: { readonly view: PrintSheet; readonly t: TFunction }) {
  return (
    <>
      {COIN_KEYS.map((coin, index) => {
        const y = COINS.rows[index] ?? 0
        const value = coin === 'gp' ? view.coins.gp : ''
        return (
          <div key={coin}>
            <Slot
              box={[
                COINS.tab.x,
                y + (COINS.value.h - COINS.tab.h) / 2,
                COINS.tab.w - 0.6,
                COINS.tab.h,
              ]}
              size={6}
              kind="label"
            >
              {t(`print.coin.${coin}`)}
            </Slot>
            <Slot
              box={[COINS.value.x + 1, y, COINS.value.w - 2, COINS.value.h]}
              size={fitPt(value, COINS.value.w - 6, 11, 7, 'number')}
              kind="number"
              {...(coin === 'gp' ? { testId: 'print-coins-gp' } : {})}
            >
              {value}
            </Slot>
          </div>
        )
      })}
    </>
  )
}

/** Строка счётчика: название, сколько осталось, сколько всего, клетки под карандаш. */
function ResourceRow({
  resource,
  x,
  top,
}: {
  readonly resource: PrintResource
  readonly x: number
  readonly top: number
}) {
  const { name, current, max, ticks } = RESOURCE_ROW
  const row = BOTTOM.row
  const boxes = resource.max <= ticks.most ? resource.max : 0
  return (
    <div data-resource={resource.id} data-testid="print-resource">
      <div
        className="print-slot print-stack print-align-start"
        style={at([x + name.x, top, name.w, row])}
      >
        <span
          className="print-slot-text print-text"
          style={{ fontSize: `${fitPt(resource.name, name.w, 7.5, 5)}pt` }}
          data-testid="print-resource-name"
        >
          {resource.name}
        </span>
        <span
          className="print-slot-text print-text print-note"
          style={{ fontSize: `${fitPt(resource.recovery, name.w, 4.8, 4)}pt` }}
        >
          {resource.recovery}
        </span>
      </div>
      <svg
        className="print-mark"
        style={at([x + current.x, top + 0.6, current.w, current.h])}
        viewBox={`0 0 ${current.w} ${current.h}`}
        aria-hidden="true"
      >
        <polygon
          points={chamfered([0.3, 0.3, current.w - 0.6, current.h - 0.6], 1)}
          strokeWidth={STROKE.medium}
          className="print-paper"
        />
      </svg>
      <Slot
        box={[x + current.x, top + 0.6, current.w, current.h]}
        size={9}
        kind="number"
        testId="print-resource-current"
      >
        {resource.current}
      </Slot>
      <Slot
        box={[x + max.x, top, max.w, row]}
        size={8}
        kind="number"
        testId="print-resource-max"
      >
        {resource.max}
      </Slot>
      {boxes > 0 && (
        <svg
          className="print-mark"
          style={at([
            x + ticks.x,
            top + (row - ticks.size) / 2,
            boxes * (ticks.size + ticks.gap),
            ticks.size,
          ])}
          viewBox={`0 0 ${boxes * (ticks.size + ticks.gap)} ${ticks.size}`}
          aria-hidden="true"
        >
          {Array.from({ length: boxes }, (_, index) => {
            const left = index * (ticks.size + ticks.gap)
            const spent = index >= resource.current
            return (
              <g key={index} {...(spent ? { 'data-spent': '' } : {})}>
                <rect
                  x={left + 0.1}
                  y={0.1}
                  width={ticks.size - 0.2}
                  height={ticks.size - 0.2}
                  strokeWidth={STROKE.thin}
                  className="print-paper"
                />
                {spent && (
                  <path
                    d={`M ${left + 0.3} 0.3 L ${left + ticks.size - 0.3} ${ticks.size - 0.3} M ${left + ticks.size - 0.3} 0.3 L ${left + 0.3} ${ticks.size - 0.3}`}
                    strokeWidth={STROKE.thin}
                  />
                )}
              </g>
            )
          })}
        </svg>
      )}
    </div>
  )
}

/** Линейки под заметки — в столбце, который счётчикам не понадобился. */
function NoteLines({ x }: { readonly x: number }) {
  const width = BOTTOM.columnWidth
  const lines = [0, 1, 2, 3, 4, 5]
  return (
    <svg
      className="print-mark"
      style={at([x, BOTTOM.firstRow, width, 5 * BOTTOM.row])}
      viewBox={`0 0 ${width} ${5 * BOTTOM.row}`}
      aria-hidden="true"
    >
      {lines.map((line) => (
        <line
          key={line}
          x1={0}
          x2={width}
          y1={line * 5.2 + 3}
          y2={line * 5.2 + 3}
          strokeWidth={STROKE.thin}
        />
      ))}
    </svg>
  )
}

function Resources({ view, t }: { readonly view: PrintSheet; readonly t: TFunction }) {
  const perColumn = 5
  const used = Math.ceil(view.resources.length / perColumn)
  const title =
    view.resources.length === 0 ? t('print.label.notes') : t('print.label.resources')
  return (
    <>
      <Slot
        box={BOTTOM.title}
        size={7}
        kind="label"
        align="start"
        testId="print-resources-title"
      >
        {title}
        {view.resourcesMore > 0 && (
          <span className="print-note print-more">
            {' '}
            · {t('print.more', { count: view.resourcesMore })}
          </span>
        )}
      </Slot>
      {BOTTOM.columns.map((x, column) => {
        if (column >= used) {
          return (
            <div key={x}>
              {column > 0 && used > 0 && (
                <Slot
                  box={[x, BOTTOM.title[1], 30, BOTTOM.title[3]]}
                  size={7}
                  kind="label"
                  align="start"
                >
                  {t('print.label.notes')}
                </Slot>
              )}
              <NoteLines x={x} />
            </div>
          )
        }
        return (
          <div key={x}>
            <Slot
              box={[
                x + RESOURCE_ROW.current.x - 1,
                BOTTOM.title[1] + 0.4,
                RESOURCE_ROW.current.w + 2,
                3.2,
              ]}
              size={4.5}
              kind="label"
            >
              {t('print.label.remaining')}
            </Slot>
            <Slot
              box={[
                x + RESOURCE_ROW.max.x,
                BOTTOM.title[1] + 0.4,
                RESOURCE_ROW.max.w,
                3.2,
              ]}
              size={4.5}
              kind="label"
            >
              {t('print.label.total')}
            </Slot>
            {view.resources
              .slice(column * perColumn, (column + 1) * perColumn)
              .map((resource, row) => (
                <ResourceRow
                  key={resource.id}
                  resource={resource}
                  x={x}
                  top={BOTTOM.firstRow + row * BOTTOM.row}
                />
              ))}
          </div>
        )
      })}
    </>
  )
}

/** Первая страница бланка: только разметка, данные — из листа. */
export function PrintSheetPage({ sheet }: { readonly sheet: CharacterSheet }) {
  const t = useT()
  const view = printSheet(t, sheet)
  return (
    <div className="print-sheet" data-testid="print-sheet">
      <Frames />
      <Header view={view} t={t} />
      <LeftColumn view={view} t={t} />
      <Combat view={view} t={t} />
      <Attacks view={view} t={t} />
      <Spellcasting view={view} t={t} />
      <Coins view={view} t={t} />
      <Resources view={view} t={t} />
      {/* Лист уходит из приложения на бумаге, и указание источника
          уходит вместе с ним: SRD — CC BY 4.0, иконки — CC BY 3.0. */}
      <Slot
        box={FOOTER}
        size={4.4}
        align="start"
        valign="top"
        wrap
        testId="print-footer"
      >
        {SRD_NOTICE} {t('print.icons')}
      </Slot>
    </div>
  )
}

/** Сколько миллиметров в пикселе CSS: 96 пикселей на дюйм. */
const PX_PER_MM = 96 / 25.4

/**
 * Масштаб предпросмотра: лист шириной 210 мм на телефоне не помещается,
 * и на экране он уменьшается под ширину колонки. На печать масштаб
 * не идёт — там лист всегда в натуральную величину (`print-sheet.css`).
 */
function usePreviewScale() {
  const preview = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  useEffect(() => {
    const node = preview.current
    if (node === null) return
    const measure = () => {
      // Ширина без отступов колонки: `clientWidth` их включает, и лист
      // по нему вылезал за край телефона на ширину отступа.
      const style = getComputedStyle(node)
      const width =
        node.clientWidth -
        (Number.parseFloat(style.paddingLeft) || 0) -
        (Number.parseFloat(style.paddingRight) || 0)
      if (width > 0) setScale(Math.min(1, width / (PAGE.width * PX_PER_MM)))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [])
  return { preview, scale }
}

/**
 * Экран печати: лист, кнопка печати и как вернуться.
 *
 * Пока экран открыт, на корне документа стоит `data-print="sheet"`:
 * по нему печатная таблица стилей прячет шапку приложения. Без
 * атрибута печать любого другого экрана остаётся какой была.
 */
export function PrintScreen({
  api,
  characterId,
  onBack,
  onHeading,
  print = () => {
    window.print()
  },
}: {
  readonly api: Api
  readonly characterId: string
  readonly onBack: () => void
  readonly onHeading?: ((heading: Heading | null) => void) | undefined
  /** Окно печати браузера; в тестах — подмена. */
  readonly print?: () => void
}) {
  const t = useT()
  const [sheet, setSheet] = useState<CharacterSheet | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const { preview, scale } = usePreviewScale()

  useEffect(() => {
    let cancelled = false
    void api
      .characterSheet(characterId)
      .then((next) => {
        if (!cancelled) setSheet(next)
      })
      .catch(() => {
        if (!cancelled) setFailure(t('errors.network'))
      })
    return () => {
      cancelled = true
    }
  }, [api, characterId, t])

  useEffect(() => {
    const root = document.documentElement
    root.dataset['print'] = 'sheet'
    return () => {
      delete root.dataset['print']
    }
  }, [])

  useEffect(() => {
    if (onHeading === undefined || sheet === null) return
    onHeading({ title: sheet.character.name, subtitle: t('print.title') })
    return () => {
      onHeading(null)
    }
  }, [onHeading, sheet, t])

  // Печать ждёт шрифтов: лист разложен под PT Sans, и окно печати,
  // открытое до их загрузки, выложило бы его запасным шрифтом.
  const printNow = () => {
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts
    if (fonts === undefined) {
      print()
      return
    }
    void fonts.ready.then(print, print)
  }

  return (
    <section className="print-screen" aria-labelledby="print-title">
      <div className="app-screen print-toolbar">
        <h2 id="print-title">{t('print.title')}</h2>
        <p className="app-field-hint">{t('print.hint')}</p>
        {failure !== null && (
          <p className="app-problems" role="alert">
            {failure}
          </p>
        )}
        <div className="app-actions">
          <button
            type="button"
            className="app-button"
            data-testid="print-now"
            disabled={sheet === null}
            onClick={printNow}
          >
            {t('print.print')}
          </button>
          <button
            type="button"
            className="app-button app-button-quiet"
            onClick={onBack}
          >
            {t('print.back')}
          </button>
        </div>
      </div>
      <div ref={preview} className="print-preview">
        {sheet === null ? (
          failure === null && <p className="app-screen">{t('print.loading')}</p>
        ) : (
          <div
            className="print-stage"
            style={{ '--print-scale': String(scale) } as CSSProperties}
          >
            <PrintSheetPage sheet={sheet} />
          </div>
        )}
      </div>
    </section>
  )
}
